import React, { useEffect, useState } from 'react';
import {
  Table, Button, Modal, Form, Input, Switch,
  Space, Popconfirm, message, Card, Select
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { getBranches } from '../api/branches';
import BranchSelect from '../components/common/BranchSelect';
import {
  getCourses, createCourse, updateCourse, deleteCourse,
} from '../api/courses';
import useDebounce from '../hooks/useDebounce';
import usePersistedPageSize from '../hooks/usePersistedPageSize';
import useServerSort from '../hooks/useServerSort';
import { apiErrorMessage } from '../utils/apiError';

const { Option } = Select;

const Courses = () => {
  const [courses, setCourses] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [search, setSearch] = useState('');
  const [branchFilter, setBranchFilter] = useState(null);
  const debouncedSearch = useDebounce(search, 400);
  const [form] = Form.useForm();
  const [persistedPageSize, setPersistedPageSize] = usePersistedPageSize('courses', 15);
  const [pagination, setPagination] = useState({ current: 1, pageSize: persistedPageSize, total: 0 });
  const { sortField, sortOrder, applySorter } = useServerSort();
  const navigate = useNavigate();

  const fetchCourses = async (page = 1, searchText = debouncedSearch, branch = branchFilter, perPage = pagination.pageSize, sortBy = sortField, sortDir = sortOrder ? (sortOrder === 'descend' ? 'desc' : 'asc') : null) => {
    setLoading(true);
    try {
      const response = await getCourses({ page, per_page: perPage, search: searchText, branch_id: branch, sort_by: sortBy || undefined, sort_direction: sortDir || undefined });
      setCourses(response.data.data);
      setPagination({
        current: response.data.current_page,
        pageSize: response.data.per_page,
        total: response.data.total,
      });
    } catch (error) {
      message.error('Sertifika Programları yüklenemedi.');
    } finally {
      setLoading(false);
    }
  };

  const fetchBranches = async () => {
    try {
      const response = await getBranches({ per_page: 100 });
      setBranches(response.data.data);
    } catch (error) {
      console.error('Şubeler yüklenemedi.');
    }
  };

  useEffect(() => {
    fetchCourses(1, debouncedSearch, branchFilter);
  }, [debouncedSearch]);

  useEffect(() => {
    fetchBranches();
  }, []);

  const handleSubmit = async (values) => {
    try {
      if (editingCourse) {
        await updateCourse(editingCourse.id, values);
        message.success('Sertifika Programı güncellendi.');
      } else {
        await createCourse(values);
        message.success('Sertifika Programı oluşturuldu.');
      }
      setModalOpen(false);
      form.resetFields();
      setEditingCourse(null);
      fetchCourses();
    } catch (error) {
      message.error(apiErrorMessage(error, 'İşlem başarısız.'));
    }
  };

  const handleEdit = (course) => {
    setEditingCourse(course);
    form.setFieldsValue(course);
    setModalOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      await deleteCourse(id);
      message.success('Sertifika Programı silindi.');
      fetchCourses();
    } catch (error) {
      message.error('Sertifika Programı silinemedi.');
    }
  };

  const columns = [
    {
      title: 'Sertifika Programı Adı',
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      sortOrder: sortField === 'name' ? sortOrder : null,
    },
    {
      title: 'Açıklama',
      dataIndex: 'description',
      key: 'description',
      render: (v) => v || '—',
    },
    {
      title: 'Şube',
      dataIndex: 'branch_id',
      key: 'branch_id',
      render: (branch_id) => {
        const branch = branches.find(b => b.id === branch_id);
        return branch ? branch.name : '—';
      },
    },
    {
      title: 'Durum',
      dataIndex: 'is_active',
      key: 'is_active',
      sorter: true,
      sortOrder: sortField === 'is_active' ? sortOrder : null,
      render: (is_active, record) => (
        <Switch
          checked={is_active}
          checkedChildren="Aktif"
          unCheckedChildren="Pasif"
          style={{ backgroundColor: is_active ? '#52c41a' : '#ff4d4f' }}
          onChange={async (checked) => {
            try {
              await updateCourse(record.id, { is_active: checked });
              message.success('Durum güncellendi.');
              fetchCourses();
            } catch (error) {
              message.error('Durum güncellenemedi.');
            }
          }}
        />
      ),
    },
    {
      title: 'İşlemler',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            icon={<EyeOutlined />}
            size="small"
            onClick={() => navigate(`/courses/${record.id}`)}
          >
            Projeler
          </Button>
          <Button icon={<EditOutlined />} size="small" onClick={() => handleEdit(record)}>
            Düzenle
          </Button>
          <Popconfirm
            title="Sertifika Programını silmek istediğinize emin misiniz?"
            onConfirm={() => handleDelete(record.id)}
            okText="Evet"
            cancelText="Hayır"
          >
            <Button icon={<DeleteOutlined />} danger size="small">Sil</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Sertifika Programı Yönetimi</h2>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => {
            setEditingCourse(null);
            form.resetFields();
            setModalOpen(true);
          }}
        >
          Yeni Sertifika Programı
        </Button>
      </div>

      <Card>
        <div className="flex gap-3 mb-4">
          <Input
            placeholder="Sertifika Programı ara..."
            allowClear
            style={{ width: 250 }}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Select
            placeholder="Şube filtrele"
            allowClear
            style={{ width: 180 }}
            onChange={(value) => {
              setBranchFilter(value);
              fetchCourses(1, debouncedSearch, value);
            }}
          >
            {branches.map((branch) => (
              <Option key={branch.id} value={branch.id}>{branch.name}</Option>
            ))}
          </Select>
        </div>

        <Table
          columns={columns}
          dataSource={courses}
          rowKey="id"
          loading={loading}
          onChange={(paginationInfo, _filters, sorter) => {
            const { sortField: sField, sortDirection: sDir } = applySorter(sorter);
            setPersistedPageSize(paginationInfo.pageSize);
            fetchCourses(paginationInfo.current, debouncedSearch, branchFilter, paginationInfo.pageSize, sField, sDir);
          }}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            pageSizeOptions: ['15', '30', '50', '100'],
            showTotal: (total) => `Toplam ${total} sertifika programı`,
          }}
        />
      </Card>

      <Modal
        title={editingCourse ? 'Sertifika Programı Düzenle' : 'Yeni Sertifika Programı'}
        open={modalOpen}
        onCancel={() => {
          setModalOpen(false);
          setEditingCourse(null);
          form.resetFields();
        }}
        footer={null}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item name="name" label="Sertifika Programı Adı" rules={[{ required: true, message: 'Sertifika Programı adı giriniz.' }]}>
            <Input placeholder="Robotik Kodlama" maxLength={100} />
          </Form.Item>
          <Form.Item name="description" label="Açıklama">
            <Input.TextArea rows={3} placeholder="Sertifika Programı açıklaması" />
          </Form.Item>
          <Form.Item name="branch_id" label="Şube" rules={[{ required: true, message: 'Şube seçiniz.' }]}>
            <BranchSelect branches={branches} />
          </Form.Item>
          {editingCourse && (
            <Form.Item name="is_active" label="Durum" valuePropName="checked">
              <Switch checkedChildren="Aktif" unCheckedChildren="Pasif" />
            </Form.Item>
          )}
          <Form.Item className="mb-0 flex justify-end">
            <Space>
              <Button onClick={() => { setModalOpen(false); form.resetFields(); }}>İptal</Button>
              <Button type="primary" htmlType="submit">
                {editingCourse ? 'Güncelle' : 'Oluştur'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Courses;